/**
 * [PM-SG 2026-08-11] PM Retention Grand Prix — the race model.
 * [PM-SG-LB 2026-09-18] REBUILT onto the CUMULATIVE LEADERBOARD.
 *
 * Turns the rows from /api/pm-singapore/leaderboard into everything the board draws: where each
 * car sits on the track, what colour it is, what its lane says, and the five headline figures.
 * The positioning rules are business logic — they decide what the board MEANS — not styling,
 * which is why they live here and not in a view.
 *
 * THE TRACK MODEL — ONE ZONE, AND WHY
 *   x = 0   the start line
 *   x = 1   the flag
 *   x       LEADER_X x (this PM's efficiency / the leader's efficiency), clamped 0…1
 *
 * ⚠ THE 85% GATE, THE PIT LANE AND `eligible` ARE GONE. The old board had two zones because an
 * unclassified car must never be drawn level with a classified one — that would be the board
 * stating something untrue. The cumulative table has no classification at all: every PM is
 * ranked, so there is no untruth left to guard against and a car's distance along the lane is
 * simply how much of the leader's efficiency it holds. `GATE_X`, `GATE_THRESHOLD`, `pitShadeOf`
 * and the whole pit-lane ramp went with it.
 *
 * ⚠ EVERY FIGURE IS CUMULATIVE from August 2026 up to and including `month`. September's rows
 * ARE the August+September totals — there is nothing to add up on this side.
 */

/* ─────────────────────────────────────────────────────────── the API shape ── */

/** One row of pm_singapore_leaderboard, camel-cased by the route. */
export interface LeaderRow {
  pmName: string;
  /** rank_position. 1 = best. NEVER null — there is no gate to fail. */
  position: number;
  originalPlacedInventory: number;
  /** The field average. IDENTICAL on every row of a month — a benchmark, not a personal figure. */
  effectiveInventory: number;
  placedInBank: number;
  recurringUpsell: number;
  oneTimeUpsell: number;
  totalUpsell: number;
  refundDisputeLost: number;
  disputeWon: number;
  /** placedInBank + totalUpsell - refundDisputeLost + disputeWon. Refunds are NETTED. */
  totalInBank: number;
  /** ⚠ A PERCENT — 188.4 means 188.4%, NOT 1.884. Never pass it through fmtPct(). */
  efficiencyPct: number | null;
  pmCount: number;
}

/** One coaching card, computed by the server. Ranks 4..last only. */
export interface CoachCard {
  position: number;
  pmName: string;
  efficiencyPct: number | null;
  gapMoney: number;
  gapPoints: number;
  /** This PM's retention — what the TIMING table calls PLACED IN BANK. */
  placedInBank: number;
  leaderName: string;
  aheadName: string;
  aheadPosition: number;
  gapToAhead: number;
  keptRate: number;
  fieldKeptRate: number;
  totalUpsell: number;
  fieldAvgUpsell: number;
  refundDisputeLost: number;
  disputeWon: number;
  lever: 'RETENTION' | 'UPSELL' | 'REFUNDS' | 'HOLD';
  leverWorth: number;
  /** The two levers a PM can actually act on. Negative = already ahead of the field on it. */
  retentionWorth: number;
  upsellWorth: number;
  secondLever: 'RETENTION' | 'UPSELL' | 'REFUNDS' | null;
  secondWorth: number;
}

export interface LeaderPayload {
  month: string | null;
  months: string[];
  total: number;
  pmCount: number;
  effectiveInventory: number;
  updatedAt: string | null;
  totals: {
    originalPlacedInventory: number;
    placedInBank: number;
    totalUpsell: number;
    refundDisputeLost: number;
    disputeWon: number;
    totalInBank: number;
    /** How many are at or above 100% — i.e. banked at least the field average. */
    aboveBenchmark: number;
  };
  /** ALREADY in rank order. The server sorts; re-sorting here is how two pages disagree. */
  rows: LeaderRow[];
  coaching: { groupSize: number; cards: CoachCard[] };
}

export interface AnnouncementEvent {
  id: number;
  pmName: string;
  kind: string;
  previous: number;
  newValue: number;
  increase: number;
  createdAt: string;
}

export interface AnnouncementPayload {
  month: string | null;
  count: number;
  totals: { retention: number; recurring: number; oneTime: number };
  events: AnnouncementEvent[];
}

/* ───────────────────────────────────────────────────────────── the geometry ── */

/** Where the front runner parks — just behind the flag, never on it. */
export const LEADER_X = 0.96;

/**
 * [PM-SG TOP10 2026-09-21, user] Only the top ten by efficiency are drawn: "based on the
 * efficiency show only top 10 on the dashboard". LIVE, not a fixed list — every poll takes
 * whoever holds P1-P10 at that moment, so a PM who climbs in appears and the one pushed out goes.
 *
 * ⚠ DISPLAY ONLY. The whole field is still the benchmark: effective inventory, every efficiency %,
 * every rank and the pit-wall figures are computed over everyone in the table, exactly as before.
 * Hiding a PM must not move anybody else's number.
 */
export const BOARD_SIZE = 10;

/**
 * Liveries, handed out BY FINISHING ORDER — P1 takes the first, P2 the second. A car's colour is
 * therefore a property of its POSITION, not of the person driving it: overtake somebody and the
 * two of you exchange liveries. That is the deliberate trade for what it buys — the running
 * order can be read straight off the track, and no two cars racing together can ever look alike.
 *
 * Walked in order around the wheel so neighbouring positions get neighbouring hues, which reads
 * as a gradient down the field rather than a scramble. Matte, not neon.
 *
 * ⚠ [2026-08-12] P1 IS RED, which breaks a rule this list used to hold: red belonged to the gate
 * line and the kerbs and was never a livery. Asked for directly. The shade is deliberately NOT
 * the board's RED (#E10600): it is warmer and lighter, so a red car beside red kerbs still reads
 * as two things.
 *
 * ⚠ [PM-SG-LB 2026-09-18] EXTENDED FROM 11 TO 16. Under the old model only CLASSIFIED runners
 * took a livery — never more than a handful. Every PM is ranked now, so an 11-entry list handed
 * P12 the same racing red as P1 (the assignment wraps with `i % length`), putting two identical
 * cars on one track and breaking this list's own stated rule. Sixteen covers a roster that grew
 * by two in a single month. The first eleven are byte-identical, so nothing above P11 changed.
  *
 * ⚠ [2026-09-18, user] SEEDED FROM REAL F1 LIVERIES, THEN SEPARATED.
 *
 * Two earlier attempts failed in opposite directions and both are worth not repeating. Walking
 * the hue wheel in order gave a tidy gradient but put four greens and four blues side by side -
 * the field read as "shades of blue, green and others", which is the opposite of telling cars
 * apart. Optimising chroma blindly to fix that produced hot pink and magenta, which read as a
 * toy rather than a race.
 *
 * So these are actual racing colours - the 2025 grid (Ferrari, Mercedes petronas, McLaren
 * papaya, Red Bull navy, Aston Martin green, Williams blue) plus historic liveries for the rest
 * (Lotus gold, Jordan violet, Jaguar green, Arrows brick, BRM ice, Benetton moss). Unusable as
 * they stand and deliberately not included: white, black, Kick Sauber's #00E701 and Alpine's
 * #FF63B8 - a neon and a candy pink.
 *
 * Each was then allowed to move at most 7 dE from its real hex to break ties, which is close
 * enough that every car is still recognisably that livery. The ORDER is optimised so adjacent
 * positions come from different families - that is the number a reader actually uses, because
 * they compare the car above to the car below.
 *
 * Measured, and worth re-running if anyone edits a value (scripts/../scratchpad/palette5.py):
 *   min pairwise dE2000     14.4   (was 5.2 - P6 and P16 were the same blue twice)
 *   min ADJACENT-rank dE    38.7   (was 9.3)
 *   contrast vs INK         3.75 .. 9.70, every one >= 3:1
 *   nearest reserved colour 14.1 amber, 14.4 green, 15.4 purple, 15.6 kerb red
 *
 * ⚠ The POSITION NUMBER is drawn in the livery, not just the car - so 3:1 against INK is a hard
 * floor, not a nicety. P1 sitting near the kerb red is the user's request, not a miss.
 */
export const TEAM_COLORS = [
  '#E23B32', // P1  Ferrari rosso   - the user's red, kept
  '#59AD8E', // P2  sea green
  '#A56140', // P3  Arrows brick
  '#249CA4', // P4  Mercedes petronas
  '#BB9A32', // P5  Lotus gold
  '#7E68B9', // P6  Jordan violet
  '#CFAE88', // P7  champagne
  '#4B80C0', // P8  Red Bull navy
  '#7D7F30', // P9  olive
  '#94C0FE', // P10 BRM ice
  '#61A72C', // P11 Benetton moss
  '#C65876', // P12 Alpine rose
  '#308438', // P13 Jaguar green
  '#0EA5DF', // P14 Williams blue
  '#EE8E26', // P15 McLaren papaya
  '#16806C', // P16 Aston Martin green
];

/** A car is lit in its own colour — a flat fill reads as an icon, a colour-matched bloom reads
 *  as bodywork under lights. */
export function glowOf(hex: string): string {
  const h = String(hex).replace('#', '');
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},.45)`;
}

export function hexA(hex: string, a: number): string {
  const h = String(hex).replace('#', '');
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${a})`;
}

/* ───────────────────────────────────────────────────────────── formatting ── */

export function fmtNum(v: number | null, dp = 2): string {
  if (v === null || v === undefined || !isFinite(v)) return '—';
  const s = Math.abs(v).toFixed(dp).split('.');
  s[0] = s[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (v < 0 ? '-' : '') + s.join('.');
}

/** ⚠ Multiplies by 100. For a 0…1 RATE only. `efficiencyPct` is ALREADY a percent — passing it
 *  through here prints 18836.00% on a wall. Use fmtPctValue() for that. */
export function fmtPct(v: number | null): string {
  return v === null || v === undefined ? '—' : `${(v * 100).toFixed(2)}%`;
}

/** For a value that is ALREADY a percent, e.g. efficiencyPct. */
export function fmtPctValue(v: number | null, dp = 1): string {
  return v === null || v === undefined || !isFinite(v) ? '—' : `${v.toFixed(dp)}%`;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

/** '2026-08' → 'AUGUST 2026'. */
export function monthLabel(ym: string | null): string {
  if (!ym) return '—';
  const [y, m] = ym.split('-').map(Number);
  const name = MONTH_NAMES[(m || 1) - 1] || '';
  return `${name} ${y}`.toUpperCase();
}

/* ─────────────────────────────────────────────────────────────── the codes ── */

/** Name split into letter-only parts. 'Anvesh .' → ['Anvesh']. */
function nameParts(name: string): string[] {
  return String(name).replace(/[^A-Za-z ]/g, ' ').trim().split(/\s+/).filter((q) => q.length > 0);
}

/** Initials — 'Shifa Khan' → SK. `depth` widens the surname when two PMs would otherwise land
 *  on the same code (Shobhit Srivastava and Sakshi Sharma are both SS at depth 1). */
function codeOf(name: string, depth: number): string {
  const parts = nameParts(name);
  if (!parts.length) return 'UNK';
  if (parts.length === 1) return (parts[0] + 'XX').slice(0, 2).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].slice(0, depth)).toUpperCase();
}

/* ──────────────────────────────────────────────────────────── the runners ── */

/** One PM as the board draws them. */
export interface Runner {
  name: string;
  code: string;
  color: string;
  glow: string;
  rank: number;
  /** 'P4' */
  pos: string;
  /** 0…1 along the track. */
  x: number;
  /** '143.7%' — already formatted, already a percent. */
  metric: string;
  /** 'LEADER' or '+44.70' — EFFICIENCY POINTS to P1, for the lane label beside a percent column. */
  gap: string;
  /** Dollars to P1. PARC FERMÉ shows this: a podium is about the prize, and '$11,008 OFF THE
   *  LEAD' is a sentence a PM can act on where '+44.70' is a unit nobody carries around. */
  gapMoney: number;
  /** Efficiency points to the car directly ahead. */
  interval: string;
  leader: boolean;
  /** The line under the name on the podium. */
  detail: string;
  /** placedInBank / originalPlacedInventory, or null when nothing was placed. */
  retentionRate: number | null;
  efficiencyPct: number | null;
  raw: LeaderRow;
}

export interface Kpi {
  label: string;
  value: string;
  sub: string;
  accent?: string | null;
  /** Half size, wraps, no letter-spacing — for a NAME, not a figure. */
  small?: boolean;
  /** [2026-09-18, user] About three quarters size, still a figure (nowrap, tabular). The bottom
   *  row of the pit wall is twice as wide as the top, so `min(17cqw, 40cqh)` binds on HEIGHT
   *  there and on WIDTH up top — a bottom-row value renders ~50% larger than a top-row one for
   *  no reason a reader can see. This trims one back without shrinking it to a caption. */
  mid?: boolean;
  pips?: { on: number; of: number };
}

export interface LeaderData {
  month: string;
  monthKey: string | null;
  /** 'AUGUST 2026' — the FIRST month the running totals start from.
   *  Derived from the payload's own month list, never typed into the copy: a header that
   *  hardcodes the start month becomes a lie the day the competition's floor moves. */
  startMonth: string;
  /** The WHOLE field — everyone in the benchmark, shown or not. `runners` is the top BOARD_SIZE. */
  total: number;
  /** Every ranked row, shown or not — for totals that must add up to the benchmark. */
  field: LeaderRow[];
  /** How many are at or above the benchmark. The honest successor to `classified`. */
  aboveField: number;
  effectiveInventory: number;
  runners: Runner[];
  kpis: Kpi[];
  totals: LeaderPayload['totals'];
  coaching: CoachCard[];
  updatedAt: string | null;
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** The five pit-wall figures. EXACTLY five, in this order — PitWallView's Grid places children
 *  by index with hardcoded areas, so a sixth card would land unplaced in the flow. */
function buildKpis(p: LeaderPayload, runners: Runner[]): Kpi[] {
  const t = p.totals;
  const leader = runners[0] || null;
  const withRefunds = p.rows.filter((r) => r.refundDisputeLost > 0).length;
  return [
    /* [2026-09-18, user] Placed FIRST, banked SECOND — what went out, then what came back.
       That reads as one sentence across the two widest cards on the page.

       ⚠ EFFECTIVE INVENTORY LOST ITS OWN CARD to make room, and is carried in the caption here
       instead. It must stay visible somewhere: card 3 is ABOVE THE BENCHMARK and card 4 is an
       efficiency, and BOTH are measured against this figure — a percentage with an unnamed
       denominator is a number nobody in the room can check. */
    {
      label: 'PLACED INVENTORY',
      value: `$${fmtNum(t.originalPlacedInventory, 0)}`,
      sub: `${p.pmCount} PMs  ·  BENCHMARK $${fmtNum(p.effectiveInventory, 0)} EACH`,
      accent: TEAM_COLORS[4], // petronas
    },
    {
      label: 'TOTAL IN BANK',
      value: `$${fmtNum(t.totalInBank, 0)}`,
      sub: t.originalPlacedInventory > 0
        ? `${((t.totalInBank / t.originalPlacedInventory) * 100).toFixed(0)}% OF PLACED  ·  CUMULATIVE SINCE AUGUST`
        : 'THE FIELD, CUMULATIVE SINCE AUGUST',
      accent: TEAM_COLORS[6], // blue
    },
    {
      label: 'ABOVE THE BENCHMARK',
      value: `${t.aboveBenchmark} / ${p.total}`,
      sub: 'EFFICIENCY AT OR OVER 100%',
      accent: '#00D26A',
      pips: { on: t.aboveBenchmark, of: p.total },
    },
    {
      label: 'BEST EFFICIENCY',
      value: leader ? fmtPctValue(leader.efficiencyPct) : '—',
      sub: leader ? leader.name.toUpperCase() : 'NO RUNNER',
      // Never null: a null accent falls back to RED on the pit wall, and red is spoken for.
      accent: leader ? TEAM_COLORS[0] : '#8A8A96',
    },
    {
      label: 'LOST TO REFUNDS',
      value: `$${fmtNum(t.refundDisputeLost, 0)}`,
      sub: `${withRefunds} OF ${p.total} CARRY ONE`,
      // Rose, not RED — red belongs to the kerbs and the start lights and can never be a card.
      accent: TEAM_COLORS[10],
      /* ⚠ NOT `small` — that is the NAME size and turned this into a caption. `mid` keeps it a
         figure and just takes the edge off, which is what the bottom row needs. */
      mid: true,
    },
  ];
}

/** The whole transform: leaderboard rows in, a race out. */
export function buildLeaderData(p: LeaderPayload): LeaderData {
  const field = p.rows || [];
  // Already in rank order (server-sorted), so the first BOARD_SIZE ARE P1-P10.
  const rows = field.slice(0, BOARD_SIZE);
  const shownPos = new Set(rows.map((r) => r.position));

  /* Codes: two passes, so BOTH halves of a clash widen together. If only the second of two SSs
     widened, the first would keep a code that is no longer unique. */
  const tally: Record<string, number> = {};
  rows.forEach((r) => { const b = codeOf(r.pmName, 1); tally[b] = (tally[b] || 0) + 1; });
  const seen: Record<string, boolean> = {};
  const codeFor: Record<string, string> = {};
  rows.forEach((r) => {
    const depth = tally[codeOf(r.pmName, 1)] > 1 ? 2 : 1;
    const base = codeOf(r.pmName, depth);
    let code = base; let n = 1;
    while (seen[code]) { n++; code = base + n; }
    seen[code] = true;
    codeFor[r.pmName] = code;
  });

  const leader = rows[0] || null;
  const leaderPct = leader?.efficiencyPct ?? null;
  const leaderBank = leader?.totalInBank ?? 0;

  /* A leader on zero or negative efficiency gives nothing to scale against, so the field parks
     on the start line rather than dividing by it. */
  const effX = (v: number | null): number => {
    if (leaderPct === null || leaderPct <= 0 || v === null) return 0;
    return LEADER_X * clamp(v / leaderPct, 0, 1);
  };

  const runners: Runner[] = rows.map((r, i) => {
    const prev = i > 0 ? rows[i - 1] : null;
    const gapPts = leaderPct === null || r.efficiencyPct === null ? null : leaderPct - r.efficiencyPct;
    const intPts = prev === null || prev.efficiencyPct === null || r.efficiencyPct === null
      ? null : prev.efficiencyPct - r.efficiencyPct;
    const color = TEAM_COLORS[i % TEAM_COLORS.length];
    const rate = r.originalPlacedInventory > 0 ? r.placedInBank / r.originalPlacedInventory : null;
    return {
      name: r.pmName,
      code: codeFor[r.pmName],
      color,
      glow: glowOf(color),
      rank: r.position,
      pos: `P${r.position}`,
      x: effX(r.efficiencyPct),
      metric: fmtPctValue(r.efficiencyPct),
      gap: i === 0 ? 'LEADER' : `+${(gapPts ?? 0).toFixed(2)}`,
      gapMoney: leaderBank - r.totalInBank,
      interval: i === 0 ? '—' : `+${(intPts ?? 0).toFixed(2)}`,
      leader: i === 0,
      detail: `IN BANK $${fmtNum(r.totalInBank, 0)}  ·  KEPT ${rate === null ? '—' : `${Math.round(rate * 100)}%`}`,
      retentionRate: rate,
      efficiencyPct: r.efficiencyPct,
      raw: r,
    };
  });

  /* `months` arrives newest-first, so the last entry is the earliest month the table holds —
     which is where the running totals start. Falls back to the shown month when only one
     exists, so a first-ever month reads "STARTING <itself>" rather than blank. */
  const startKey = p.months?.length ? p.months[p.months.length - 1] : p.month;

  return {
    month: monthLabel(p.month),
    monthKey: p.month,
    startMonth: monthLabel(startKey),
    total: field.length,
    field,
    aboveField: p.totals?.aboveBenchmark ?? 0,
    effectiveInventory: p.effectiveInventory,
    runners,
    kpis: buildKpis(p, runners),
    totals: p.totals,
    // The debrief walks P4 to the last SHOWN rank — a hidden PM gets no card either.
    coaching: (p.coaching?.cards ?? []).filter((c) => shownPos.has(c.position)),
    updatedAt: p.updatedAt,
  };
}

/* ───────────────────────────────────────────────── announcement presentation ── */

/** Colour by CATEGORY, not by column: green is money kept, the cyan family is money added on
 *  top, and the sub-type is carried by the label rather than by a third hue. */
export const KIND_COLORS: Record<string, string> = {
  Retention: '#00D26A',
  'Recurring Upsell': '#00A9CE',
  'One-Time Upsell': '#37BEDD',
};

/** Each maps to a real F1 honour that means the same thing the event does. */
export const KIND_HEADS: Record<string, string> = {
  Retention: 'POSITION HELD',
  'Recurring Upsell': 'DRS ACTIVATED',
  'One-Time Upsell': 'FASTEST LAP',
};

export const KIND_SUBS: Record<string, string> = {
  Retention: 'RETENTION BANKED',
  'Recurring Upsell': 'RECURRING UPSELL SECURED',
  'One-Time Upsell': 'ONE-TIME UPSELL SECURED',
};
